// Apollo Client
import ApolloClient from 'apollo-client';
import { ApolloLink, split } from 'apollo-link';
import { HttpLink } from 'apollo-link-http';
import { WebSocketLink } from 'apollo-link-ws';
import { setContext } from 'apollo-link-context';
import { Hermes } from 'apollo-cache-hermes';
import { getMainDefinition } from 'apollo-utilities';
import { withClientState } from 'apollo-link-state';
import { LocalStorageKey } from './common/Definitions';
import { getAuthToken } from './common/Auth';
import { error, connecting, connected, reconnecting, reconnected, disconnected } from './action/WebSocketAction';

// App Config
import config from './config';

// Cache Resolvers
import resolvers from './graphql/cache/resolvers';

// Apollo Client Initialize Function
// Requires Redux Store for Websocket status events
export default (store) => {
  const httpLink = new HttpLink({
    uri: config.graphqluri,
    credentials: config.apolloClientOpts.credentials,
  });
  
  // Initialize WS connection upon first subscription
  // Authorize connections via auth token
  const wsLink = new WebSocketLink({
    uri: config.subscriptionEndpoint,
    options: {
      reconnect: true,
      lazy: true,
      connectionParams: () => {
        return {
          appKey: config.appKey,
          authToken: getAuthToken(),
        };
      },
    },
  });

  // Websocket Events
  wsLink.subscriptionClient.onReconnecting(() => store.dispatch(reconnecting()));
  wsLink.subscriptionClient.onReconnected(() => store.dispatch(reconnected()));
  wsLink.subscriptionClient.onConnecting(() => store.dispatch(connecting()));
  wsLink.subscriptionClient.onConnected(() => store.dispatch(connected()));
  wsLink.subscriptionClient.onDisconnected(() => store.dispatch(disconnected()));
  wsLink.subscriptionClient.onError(() => store.dispatch(error()));
  
  const authLink = setContext((_, { headers }) => {
    // get the authentication token from local storage if it exists
    const token = getAuthToken();
    // return the headers to the context so httpLink can read them
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });
  
  const terminatingLink = split(
    ({ query }) => {
      const { kind, operation } = getMainDefinition(query);
      return (
        kind === 'OperationDefinition' && operation === 'subscription'
      );
    },
    wsLink,
    httpLink,
  );
  
  const cache = new Hermes({
    addTypename: true,
    entityIdForNode: (result) => {
      if (result.id && result.__typename) {
        return result.__typename + result.id;
      }
      return result.id || result._id;
    },
  });
  
  const stateLink = withClientState({
    cache,
    resolvers,
  });
  
  const link = ApolloLink.from([stateLink, authLink, terminatingLink]);

  return new ApolloClient({
    link,
    cache,
  });
};
